import { motion } from "framer-motion";
import moment from "moment";
import "moment/locale/it";
import { Link } from "react-router-dom";
import { useState } from "react";
import apiClient from "../api/client";
import settings from "../config/settings";
import { FaChevronRight, FaEdit, FaTrash } from "react-icons/fa";
import useAuth from "../auth/useAuth"; // Adjust the import path as needed

moment.locale("it");

export default function Machine({ name, status, commessa, logs, lastUpdated }) {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completingCommessa, setCompletingCommessa] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({
    speed: 0,
    timeRemaining: 0,
    loaded: 0,
    total: 0,
    startTime: null,
    lastLoaded: 0,
    lastTime: Date.now(),
    speedHistory: [],
  });
  const [formData, setFormData] = useState({
    nome: "",
    data_ora: "",
    files: [],
    pezzi: 0,
    nome_cliente: "",
    note: "",
  });
  const [error, setError] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [replyText, setReplyText] = useState("");
  const [commessaId, setCommessaId] = useState(null);

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0 || !isFinite(seconds)) return "Calcolo...";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  };

  const formatSpeed = (bytesPerSecond) => {
    if (!bytesPerSecond || bytesPerSecond === 0) return "0 B/s";
    return `${formatSize(bytesPerSecond)}/s`;
  };

  const calculateAverageSpeed = (newSpeed, speedHistory) => {
    // Keep last 5 speed measurements for moving average
    const history = [...speedHistory.slice(-4), newSpeed];
    // Calculate weighted average with more weight to recent speeds
    const weights = [0.1, 0.15, 0.2, 0.25, 0.3];
    const weightedSum = history.reduce(
      (sum, speed, i) => sum + speed * weights[i + (5 - history.length)],
      0
    );
    const weightSum = weights
      .slice(5 - history.length)
      .reduce((a, b) => a + b, 0);
    return weightedSum / weightSum;
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Calculate total size
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    const maxSize = 1024 * 1024 * 1024; // 1GB

    if (totalSize > maxSize) {
      setError(
        `La dimensione totale dei file non può superare 1GB. Dimensione attuale: ${(
          totalSize /
          (1024 * 1024)
        ).toFixed(2)}MB`
      );
      e.target.value = null; // Reset file input
      return;
    }

    setError(null);
    setFormData((prev) => ({ ...prev, files }));
  };

  const handleDownloadFile = async (fileId) => {
    try {
      setDownloadProgress((prev) => ({ ...prev, [fileId]: 0 }));

      const response = await apiClient.get(
        `api/commesse/files/${fileId}`,
        {},
        {
          responseType: "blob",
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setDownloadProgress((prev) => ({
                ...prev,
                [fileId]: percentCompleted,
              }));
            }
          },
        }
      );

      if (response.ok) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = commessa.files.find(
          (f) => f.id === fileId
        ).original_filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error("Error downloading file");
        setError("Errore durante il download del file");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      setError("Errore durante il download del file");
    } finally {
      // Clear progress after a short delay
      setTimeout(() => {
        setDownloadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
      }, 1000);
    }
  };

  const handleCompleteCommessa = async (commessaId) => {
    if (!commessaId) return;

    try {
      setCompletingCommessa(true);
      const response = await apiClient.patch(
        `api/commesse/${commessaId}/status`,
        {
          status: "completata",
        }
      );

      if (response.ok) {
        // Refresh the page to update the data
        window.location.reload();
      } else {
        setError(
          response.data?.error ||
            "Errore durante il completamento della commessa"
        );
      }
    } catch (error) {
      console.error("Error completing commessa:", error);
      setError(
        error.response?.data?.error ||
          "Errore durante il completamento della commessa"
      );
    } finally {
      setCompletingCommessa(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setUploadProgress(0);
    setUploadStats({
      speed: 0,
      timeRemaining: 0,
      loaded: 0,
      total: 0,
      startTime: Date.now(),
      lastLoaded: 0,
      lastTime: Date.now(),
      speedHistory: [],
    });

    try {
      const totalSize = formData.files.reduce(
        (acc, file) => acc + file.size,
        0
      );
      const maxSize = 1024 * 1024 * 1024; // 1GB

      if (totalSize > maxSize) {
        throw new Error(
          `La dimensione totale dei file non può superare 1GB. Dimensione attuale: ${(
            totalSize /
            (1024 * 1024)
          ).toFixed(2)}MB`
        );
      }

      const formDataToSend = new FormData();
      formDataToSend.append("nome", formData.nome);
      formDataToSend.append(
        "data_ora",
        formData.data_ora
          ? moment(formData.data_ora).format("YYYY-MM-DD HH:mm:ss")
          : moment().format("YYYY-MM-DD HH:mm:ss")
      );
      formDataToSend.append("machine_name", name);
      formDataToSend.append("pezzi", formData.pezzi);
      formDataToSend.append("nome_cliente", formData.nome_cliente);
      formDataToSend.append("note", formData.note);
      formData.files.forEach((file) => {
        formDataToSend.append("files", file);
      });

      const response = await apiClient.post("/api/commesse", formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const currentTime = Date.now();
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );

            // Calculate instantaneous speed
            const timeDiff = (currentTime - uploadStats.lastTime) / 1000;
            const loadedDiff = progressEvent.loaded - uploadStats.lastLoaded;
            const instantSpeed = loadedDiff / timeDiff;

            // Calculate average speed using moving average
            const avgSpeed = calculateAverageSpeed(
              instantSpeed,
              uploadStats.speedHistory || []
            );

            // Calculate time remaining based on average speed
            const remainingBytes = progressEvent.total - progressEvent.loaded;
            const timeRemaining = avgSpeed > 0 ? remainingBytes / avgSpeed : 0;

            setUploadProgress(percentCompleted);
            setUploadStats((prev) => ({
              ...prev,
              speed: avgSpeed,
              timeRemaining,
              loaded: progressEvent.loaded,
              total: progressEvent.total,
              lastLoaded: progressEvent.loaded,
              lastTime: currentTime,
              speedHistory: [
                ...(prev.speedHistory || []).slice(-4),
                instantSpeed,
              ],
            }));
          }
        },
        timeout: 3600000, // 1 hour
      });

      if (response.ok) {
        setIsModalOpen(false);
        setFormData({
          nome: "",
          data_ora: "",
          files: [],
          pezzi: 0,
          nome_cliente: "",
          note: "",
        });
        window.location.reload();
      } else {
        throw new Error(
          response.data?.error || "Errore durante il salvataggio della commessa"
        );
      }
    } catch (error) {
      console.error("Error submitting commessa:", error);
      setError(error.message || "Errore durante il salvataggio della commessa");
    } finally {
      setLoading(false);
      setUploadProgress(0);
      setUploadStats({
        speed: 0,
        timeRemaining: 0,
        loaded: 0,
        total: 0,
        startTime: null,
        lastLoaded: 0,
        lastTime: Date.now(),
        speedHistory: [],
      });
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    if (!id) return;
    setCommessaId(id);

    try {
      const response = await apiClient.patch(`api/commesse/${id}/status`, {
        status: newStatus,
      });

      if (response.ok) {
        window.location.reload();
      } else {
        setError(
          response.data?.error ||
            "Errore durante il cambiamento dello stato della commessa"
        );
      }
    } catch (error) {
      console.error("Error changing status:", error);
      setError(
        error.response?.data?.error ||
          "Errore durante il cambiamento dello stato della commessa"
      );
    }
  };

  const handleAddReply = async (id) => {
    if (!replyText.trim()) return;

    try {
      const response = await apiClient.post(`api/commesse/${id}/replies`, {
        message: replyText.trim(),
        operator: user.username || user.email || "Operatore", // Use username or fallback to email
      });

      if (response.ok) {
        setReplyText("");
        window.location.reload();
      } else {
        setError(
          response.data?.error || "Errore durante l'aggiunta della nota"
        );
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      setError(
        error.response?.data?.error || "Errore durante l'aggiunta della nota"
      );
    }
  };

  const handleDelete = async (commessaId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa commessa?"))
      return;

    try {
      const response = await apiClient.delete(`api/commesse/${commessaId}`);
      if (response.ok) {
        window.location.reload();
      } else {
        setError("Errore durante l'eliminazione della commessa");
      }
    } catch (error) {
      console.error("Error deleting commessa:", error);
      setError("Errore durante l'eliminazione della commessa");
    }
  };

  const handleEdit = (commessa) => {
    setFormData({
      nome: commessa.nome,
      data_ora: moment(commessa.data_ora).format("YYYY-MM-DDTHH:mm"),
      pezzi: commessa.pezzi,
      note: commessa.note || "",
      nome_cliente: commessa.nome_cliente || "",
    });
    setIsModalOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETATO":
        return "bg-green-100 text-green-800 border border-green-200";
      case "IN STAMPA":
      case "IN LAVORAZIONE":
        return "bg-blue-100 text-blue-800 border border-blue-200";
      case "STANDBY":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "ALLARME":
        return "bg-red-100 text-red-800 border border-red-200";
      case "OFFLINE":
        return "bg-gray-100 text-gray-800 border border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const getStatusText = (status) => {
    switch (status?.toUpperCase()) {
      case "COMPLETATO":
        return "Completato";
      case "IN STAMPA":
        return "In Stampa";
      case "IN LAVORAZIONE":
        return "In Lavorazione";
      case "STANDBY":
        return "Standby";
      case "ALLARME":
        return "⚠️ Allarme";
      case "OFFLINE":
        return "Offline";
      default:
        return status || "N/D";
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-white rounded-lg shadow-lg p-6 w-full hover:shadow-xl transition-all duration-300 border border-gray-100"
      >
        {/* Machine Header */}
        <div className="flex justify-between items-center mb-6 flex-wrap">
          <div className="flex items-center space-x-3">
            <motion.h3
              whileHover={{ scale: 1.02 }}
              className="text-2xl font-bold text-gray-800 hover:text-blue-600 transition-colors"
            >
              {name}
            </motion.h3>
            <motion.span
              whileHover={{ scale: 1.05 }}
              className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                status
              )}`}
            >
              {getStatusText(status)}
            </motion.span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{moment(lastUpdated).fromNow()}</span>
          </div>
        </div>

        {/* Current Commessa */}
        <motion.div className="mb-6 bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-colors duration-200">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-600">
              Commessa Attiva
            </label>
            {commessa && (
              <>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(commessa)}
                    className="btn btn-ghost btn-sm"
                  >
                    <FaEdit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(commessa.id)}
                    className="btn btn-ghost btn-sm text-red-600 hover:text-red-800"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                  {commessa.stato === "in_attesa" && (
                    <button
                      onClick={() =>
                        handleStatusChange(commessa.id, "in_corso")
                      }
                      className="btn btn-sm btn-primary"
                    >
                      In lavorazione
                    </button>
                  )}
                  {commessa.stato === "in_corso" && (
                    <button
                      onClick={() =>
                        handleStatusChange(commessa.id, "completata")
                      }
                      className="btn btn-sm btn-success"
                    >
                      Completato
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          {commessa ? (
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-start">
                  {" "}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 text-lg">
                      {commessa.nome}
                      {commessa.nome_cliente && (
                        <span className="text-sm text-gray-600 ml-2">
                          Cliente: {commessa.nome_cliente}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <p className="font-semibold text-gray-800 text-md">
                  {commessa.pezzi} pezzi
                </p>
                <div className="mt-2 text-sm text-gray-600">
                  <p>Data: {moment(commessa.data_ora).format("LLL")}</p>
                  <p>
                    Stato:{" "}
                    {commessa.stato == "in_corso"
                      ? "In Corso"
                      : commessa.stato == "in_attesa"
                      ? "In attesa"
                      : "In corso"}
                  </p>
                  {commessa.files && commessa.files.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium mb-1">File allegati:</p>
                      <ul className="space-y-2">
                        {commessa.files.map((file, idx) => {
                          if (file.id)
                            return (
                              <li key={idx} className="flex flex-col space-y-1">
                                <button
                                  onClick={() => handleDownloadFile(file.id)}
                                  className="text-blue-600 hover:text-blue-800 flex items-center"
                                  disabled={
                                    downloadProgress[file.id] !== undefined
                                  }
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4 mr-1"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                    />
                                  </svg>
                                  {file.original_filename}
                                </button>
                                {downloadProgress[file.id] !== undefined && (
                                  <div className="w-full">
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                        style={{
                                          width: `${
                                            downloadProgress[file.id]
                                          }%`,
                                        }}
                                      ></div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      Download in corso:{" "}
                                      {downloadProgress[file.id]}%
                                    </div>
                                  </div>
                                )}
                              </li>
                            );
                        })}
                      </ul>
                    </div>
                  )}
                  {commessa.note && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Note:
                      </h4>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700">{commessa.note}</p>
                      </div>
                    </div>
                  )}
                  {commessa.replies && commessa.replies.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        Note operatori:
                      </h4>
                      <div className="space-y-2">
                        {commessa.replies.map((reply, idx) => (
                          <div
                            key={idx}
                            className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm"
                          >
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium text-gray-700">
                                {reply.operator}
                              </span>
                              <span className="text-xs text-gray-500">
                                {moment(reply.timestamp).format(
                                  "DD/MM/YY HH:mm"
                                )}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {reply.message}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(commessa.stato === "in_attesa" ||
                    commessa.stato === "in_corso") && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleAddReply(commessa.id);
                      }}
                      className="mt-4"
                    >
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          className="input input-bordered input-sm flex-1"
                          placeholder="Aggiungi una nota..."
                        />
                        <button
                          type="submit"
                          disabled={!replyText.trim()}
                          className="btn btn-primary btn-sm"
                        >
                          Invia
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Nessuna commessa attiva</p>
          )}
          {error && (
            <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}
        </motion.div>

        {/* Logs Section */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-600 mb-3 flex items-center">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            Attività Recenti
          </h4>
          <motion.div
            className="space-y-3"
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}
          >
            {logs.map((log, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, x: -20 },
                  visible: { opacity: 1, x: 0 },
                }}
                className="text-sm border-l-2 border-blue-200 pl-4 py-2 hover:border-blue-500 transition-colors duration-200 bg-gray-50 rounded-r-lg"
              >
                <p className="text-blue-600 font-medium mb-1">
                  {moment(log.timestamp).format("LLL")}
                </p>
                <p className="text-gray-700">{log.message}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
          {/* Primary Action */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsModalOpen(true)}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg transition-all duration-200 font-medium shadow-md hover:shadow-lg flex items-center justify-center gap-2 order-first"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span>Aggiungi Commessa</span>
          </motion.button>

          {/* Secondary Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Link to={`/commesse/${name}`} className="flex-1">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 py-3 px-4 rounded-lg transition-all duration-200 font-medium border border-gray-200 flex items-center justify-center gap-2 shadow-sm hover:shadow"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                <span>Storico</span>
              </motion.button>
            </Link>

            <Link to={`/machine/${name.toLowerCase()}`} className="flex-1">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 py-3 px-4 rounded-lg transition-all duration-200 font-medium border border-gray-200 flex items-center justify-center gap-2 shadow-sm hover:shadow"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>Dettagli</span>
              </motion.button>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                Nuova Commessa - {name}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
                className={`text-gray-400 hover:text-gray-500 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Commessa
                </label>
                <input
                  type="text"
                  required
                  disabled={loading}
                  value={formData.nome}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, nome: e.target.value }))
                  }
                  className={`input input-bordered w-full ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  placeholder="Inserisci il nome della commessa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numero pezzi
                </label>
                <input
                  type="text"
                  required
                  disabled={loading}
                  value={formData.pezzi}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, pezzi: e.target.value }))
                  }
                  className={`input input-bordered w-full ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  placeholder="Inserisci il numero di pezzi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File (.pdf, .prt, .anc)
                </label>
                <input
                  type="file"
                  multiple
                  disabled={loading}
                  onChange={handleFileChange}
                  className={`file-input file-input-bordered w-full ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Puoi selezionare più file
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Cliente
                </label>
                <input
                  type="text"
                  value={formData.nome_cliente || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      nome_cliente: e.target.value,
                    }))
                  }
                  className="input input-bordered w-full"
                  placeholder="Nome del cliente"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data e ora inizio
                </label>
                <input
                  type="datetime-local"
                  required
                  disabled={loading}
                  value={
                    formData.data_ora || moment().format("YYYY-MM-DDTHH:mm")
                  }
                  onChange={(e) => {
                    const date = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      data_ora: date, // Store the raw datetime-local value
                    }));
                  }}
                  className="input input-bordered w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note
                </label>
                <textarea
                  value={formData.note || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, note: e.target.value }))
                  }
                  className="textarea textarea-bordered w-full"
                  placeholder="Note aggiuntive"
                  rows={3}
                />
              </div>

              {/* Progress bar in modal */}
              {loading && uploadProgress > 0 && (
                <div className="w-full">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Caricamento in corso...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>
                      {formatSize(uploadStats.loaded)} /{" "}
                      {formatSize(uploadStats.total)}
                    </span>
                    <span>
                      {formatSpeed(uploadStats.speed)} •{" "}
                      {formatTime(uploadStats.timeRemaining)} rimanenti
                    </span>
                  </div>
                </div>
              )}

              {error && (
                <div className="text-red-500 text-sm bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={loading}
                  className={`btn btn-ghost ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`btn btn-primary ${loading ? "loading" : ""}`}
                >
                  {loading ? "Salvataggio..." : "Salva"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </>
  );
}
